import { Button, Card, Slider, Stack, Text } from "@mantine/core";
import { useThree } from "@react-three/fiber";
import { forwardRef, useImperativeHandle, useRef } from "react";
import ControlPanel from "../ControlPanel";

const FILENAME = "stool";

interface IStoolProps {
  primaryColor: string;
}

interface IStoolEditorProps {
  primaryColor: string;
}

interface IDownloadHandle {
  download: () => void;
}

const StoolInternals = ({ primaryColor }: IStoolProps) => {
  return (
    <>
      <mesh
        scale={[1.8, 1.8, 1.8]}
        position={[0, -0.8, 0]}
        rotation={[0, Math.PI / 6, 0]}
      >
        <mesh position={[0, 1, 0]}>
          <cylinderGeometry args={[0.6, 0.6, 0.15]} />
          <meshStandardMaterial color={primaryColor} />
        </mesh>
        <mesh position={[0.4, 0.5, 0]} rotation={[0, 0, Math.PI / 12]}>
          <cylinderGeometry args={[0.1, 0.07, 1]} />
          <meshStandardMaterial color={primaryColor} />
        </mesh>
        <mesh
          position={[-0.2, 0.5, -0.35]}
          rotation={[0, (Math.PI * 2) / 3, Math.PI / 12]}
        >
          <cylinderGeometry args={[0.1, 0.07, 1]} />
          <meshStandardMaterial color={primaryColor} />
        </mesh>

        <mesh
          position={[-0.2, 0.5, 0.35]}
          rotation={[0, (Math.PI * 4) / 3, Math.PI / 12]}
        >
          <cylinderGeometry args={[0.1, 0.07, 1]} />
          <meshStandardMaterial color={primaryColor} />
        </mesh>
      </mesh>
    </>
  );
};

export const Stool = ({ primaryColor }: IStoolProps) => {
  return <StoolInternals primaryColor={primaryColor} />;
};

const DownloadableStool = forwardRef(({ primaryColor }: IStoolProps, ref) => {
  const gl = useThree((state) => state.gl);

  useImperativeHandle(ref, () => ({
    download() {
      const link = document.createElement("a");
      link.setAttribute("download", `${FILENAME}.png`);
      link.setAttribute(
        "href",
        gl.domElement
          .toDataURL("image/png")
          .replace("image/png", "image/octet-stream")
      );
      link.click();
    },
  }));

  return <StoolInternals primaryColor={primaryColor} />;
});

export const StoolEditor = ({ primaryColor }: IStoolEditorProps) => {
  const childRef = useRef<IDownloadHandle>();

  return (
    <ControlPanel
      onClick={() => {
        if (childRef && childRef.current) {
          childRef.current.download();
        }
      }}
      scene={<Stool primaryColor={primaryColor} />}
      downloadable={
        <DownloadableStool ref={childRef} primaryColor={primaryColor} />
      }
    ></ControlPanel>
  );
};
